import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, PlusCircle, ChevronRight, ArrowRight, Loader2 } from "lucide-react";
import { listComplaints, type Complaint, type ComplaintStudent } from "../services/teacherApi";

// Keep each side to one name + overflow count so a group complaint never widens
// the card past the viewport.
const summarise = (list: ComplaintStudent[]) =>
  list.length === 0 ? "—" : list.length === 1 ? list[0].name : `${list[0].name} +${list.length - 1}`;

// Home-dashboard widget: open count + a couple of recent complaints, a Register
// button (opens the modal), and a link to the full registry. Self-fetches so it
// doesn't couple to the dashboard's main data load.
export default function ComplaintsWidget() {
  const [complaints, setComplaints] = useState<Complaint[] | null>(null);

  useEffect(() => {
    listComplaints().then((r) => setComplaints(r.complaints)).catch(() => setComplaints([]));
  }, []);

  const open = (complaints || []).filter((c) => c.status === "open");
  const recent = (complaints || []).slice(0, 2);

  return (
    <section className="glass-panel rounded-xl px-4 py-3.5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-mono text-[10px] text-cobalt-400 uppercase tracking-widest flex items-center gap-1.5">
          <ShieldAlert size={11}/> Complaints
        </h2>
        {open.length > 0 && (
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 uppercase tracking-widest">
            {open.length} open
          </span>
        )}
      </div>

      {complaints === null ? (
        <div className="flex items-center gap-2 text-slate-500 text-xs py-2">
          <Loader2 size={12} className="animate-spin"/> Loading…
        </div>
      ) : recent.length === 0 ? (
        <p className="text-[11px] text-slate-500 mb-3">No complaints registered yet.</p>
      ) : (
        <div className="space-y-1.5 mb-3">
          {recent.map((c) => (
            <Link key={c.id} to="/complaints" className="block px-3 py-2 rounded-lg border border-white/10 hover:bg-white/[0.04] overflow-hidden">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="min-w-0 max-w-[45%] truncate text-[11px] text-cobalt-200">{summarise(c.complainants)}</span>
                <ArrowRight size={11} className="shrink-0 text-slate-500"/>
                <span className="min-w-0 flex-1 truncate text-[11px] text-madder-200">{summarise(c.accused)}</span>
                <span className={`shrink-0 font-mono text-[8px] uppercase tracking-widest ${c.status === "open" ? "text-amber-300" : "text-slate-500"}`}>{c.status}</span>
              </div>
              <div className="text-[11px] text-slate-400 truncate mt-0.5">{c.about}</div>
            </Link>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <Link to="/complaints/new" state={{ from: "/" }}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-cobalt-600 hover:bg-cobalt-500 text-oncolor text-[11px] font-mono uppercase tracking-widest">
          <PlusCircle size={12}/> Register
        </Link>
        <Link to="/complaints"
          className="flex items-center justify-center gap-1 py-2 px-3 rounded-lg border border-white/10 text-slate-300 text-[11px] font-mono uppercase tracking-widest hover:bg-white/[0.03]">
          Registry <ChevronRight size={12}/>
        </Link>
      </div>
    </section>
  );
}
